import React, { useMemo, useEffect, useCallback, Suspense, Fragment } from 'react';
import { observer } from 'mobx-react-lite';
import { getCalculatedOrderRow } from './ConfirmationOrderRow';
import _ from 'lodash';
import TableHeader from './TableHeader';
import OrderTotalSummary from './OrderTotalSummary';
import ConfirmationOrderRow from './ConfirmationOrderRow';
import PersonDetailsSummary from './PersonDetailsSummary';
import HotelDetails from './HotelDetails';
import PaymentDetails from './PaymentDetails';

export const OrderSummary = observer(props => {
  const { displayConfirmationSettings, breakouts, orders, settings, mainEvent, hotelStore, ordersLoaded, environment } = props;

  const hotelAllotments = hotelStore?.hotelAllotments;
  const hotels = hotelStore?.hotels;

  const {
    overrideDisplay = false,
    showOrderDetails = true,
    showPersonDetails = false,
    showHotelDetails = false,
    showOrderRowsFromDisplayGroup = false,
    displayGroupFilter = null,
    showPaymentDetails = false,
  } = settings;

  useEffect(() => {
    if (!hotelStore.loaded && ordersLoaded && environment.eventId && hotels?.length === 0 && orders?.some(o => o?.orderRow?.some(or => or.hotelGroupId))) {
      hotelStore?.fetchHotels(environment.eventId);
    }
  }, [hotelAllotments, hotelAllotments, ordersLoaded, environment]);

  const overridenDisplaySettings = useMemo(() => {
    const o = {};
    for (const key in displayConfirmationSettings) {
      o[key] = settings?.[_.camelCase(key)] === true;
    }
    return o;
  }, [settings, displayConfirmationSettings]);

  const flatOrderRowsList = useMemo(() => {
    const showCancelledRows =
      (overrideDisplay && overridenDisplaySettings['ShowConfCancelledRows']) || (!overrideDisplay && displayConfirmationSettings['ShowConfCancelledRows']);

    const orderRows = orders
      .filter(o => o.orderRow)
      .map(o => o.orderRow.filter(or => (showCancelledRows ? true : or.status != 'Cancelled')))
      .flat();

    const supplementEvents = breakouts.filter(b => b.type === 'ALLOTMENT_SUPPLEMENT');

    const orderRowsWithHotel =
      hotelAllotments?.length > 0
        ? orderRows
            .filter(or => or.hotelAllotmentPricingId)
            .reduce((acc, orderRow) => {
              const hotelAllotment = hotelAllotments?.find(ha =>
                ha.hotelAllotmentPricing?.some(hap => hap.allotmentPricingId === orderRow.hotelAllotmentPricingId)
              );

              const hotel = hotels.find(h => h.hotelRoomType.some(y => y.hotelRoomTypeId == hotelAllotment.hotelRoomTypeId));
              const roomType = hotel.hotelRoomType.find(hr => hr.hotelRoomTypeId == hotelAllotment.hotelRoomTypeId);

              const pricingIds = orderRows.filter(or => or.hotelAllotmentPricingId === orderRow.hotelAllotmentPricingId).map(or => or.hotelAllotmentPricingId);

              const dateValues = hotelAllotments
                .filter(ha => ha.hotelAllotmentPricing?.some(hap => pricingIds.includes(hap.allotmentPricingId)))
                .map(ha => +new Date(ha.date.substring(0, 10)));

              const allotmentStartDate = Math.min(...dateValues);
              const allotmentEndDate = Math.max(...dateValues);

              const supplementId = hotelAllotment?.supplementId;

              const checkinDate = new Date(allotmentStartDate).toISOString().substring(0, 10);
              const checkoutDate = new Date(new Date(allotmentEndDate).setDate(new Date(allotmentEndDate).getDate() + 1)).toISOString().substring(0, 10);

              if (!acc.some(a => a.hotelAllotmentPricingId === orderRow.hotelAllotmentPricingId)) {
                const rowTotal = orderRows
                  ?.filter(or => or.hotelAllotmentPricingId === orderRow.hotelAllotmentPricingId)
                  ?.reduce((acc, or) => acc + or.amount, 0 ?? 0);

                const vatAmount =
                  orderRows
                    ?.filter(or => or.hotelAllotmentPricingId === orderRow.hotelAllotmentPricingId)
                    ?.reduce((acc, or) => acc + (or.vatAmount ?? 0), 0 ?? 0) ?? 0;

                const quantity = orderRows.filter(or => or.hotelAllotmentPricingId === orderRow.hotelAllotmentPricingId).length;

                acc.push({
                  ...orderRow,
                  vatAmount: vatAmount,
                  rowTotal: rowTotal + vatAmount,
                  amount: orderRow.amount ?? 0,
                  name: ` ${hotel.name} - ${roomType.name}`,
                  startDateTime: checkinDate,
                  supplementId,
                  hotelGroupId: orderRow.hotelGroupId,
                  endDateTime: checkoutDate,
                  quantity,
                  grouped: true,
                });

                const groupLength = acc.filter(a => a.hotelGroupId === orderRow.hotelGroupId).length;
                const orderGroupLength = orderRows.filter(or => or.hotelGroupId === orderRow.hotelGroupId).length;

                if (!acc.some(a => a.eventId === supplementId) && supplementId && groupLength === orderGroupLength) {
                  const supplementRow = orderRows.find(or => or.eventId === supplementId);

                  if (supplementRow) {
                    const supplementEvent = supplementEvents.find(se => se.eventId === supplementId);

                    const supplementRowTotal = supplementRow.amount ?? 0;
                    const supplementRowVatAmount = supplementRow.vatAmount ?? 0;
                    const supplementQuantity = supplementRow?.quantity ?? 1;

                    acc.push({
                      ...supplementRow,
                      eventId: supplementId,
                      vatAmount: supplementRowVatAmount,
                      rowTotal: supplementRowTotal + supplementRowVatAmount,
                      amount: supplementRow.amount ?? 0,
                      name: supplementEvent?.name,
                      startDateTime: checkinDate,
                      supplementId,
                      endDateTime: checkoutDate,
                      quantity: supplementQuantity,
                    });
                  }
                }
              }

              return acc;
            }, []) ?? []
        : [];


    const regularOrderRows = orderRows.filter(or => !or.hotelAllotmentPricingId && !supplementEvents.some(se => se.eventId === or.eventId));

    if (showOrderRowsFromDisplayGroup && displayGroupFilter) {
      const displayGroup = displayGroupFilter;
      const displayGroupEvents = breakouts.filter(b => b.displayGroup === displayGroup);

      const displayGroupOrderRows = regularOrderRows.filter(or => displayGroupEvents.some(dge => dge.eventId === or.eventId));

 
      return [...displayGroupOrderRows].map(or => getCalculatedOrderRow(or, breakouts, orders, or.orderId, mainEvent));
    }

    return [...regularOrderRows, ...orderRowsWithHotel].map(or => getCalculatedOrderRow(or, breakouts, orders, or.orderId, mainEvent));
  }, [orders, breakouts, hotels, hotelAllotments, mainEvent, overrideDisplay, overridenDisplaySettings, displayConfirmationSettings]);

  const showConfOrderTotalSum = useMemo(
    () =>
      !!(
        showOrderDetails &&
        flatOrderRowsList?.length &&
        ((overrideDisplay && overridenDisplaySettings['ShowConfOrderTotalSum']) || (!overrideDisplay && displayConfirmationSettings['ShowConfOrderTotalSum']))
      ),
    [overrideDisplay, overridenDisplaySettings, displayConfirmationSettings, flatOrderRowsList, showOrderDetails]
  );

  const showConfPersonDetails = useCallback(
    order => {
      const hasPersonValues = Object.entries(order?.person ?? {})?.some(([_, value]) => value);
      const hasCompanyValues = Object.entries(order?.company ?? {})?.some(([_, value]) => value);
      const hasInvoiceValues = Object.entries(order?.invoice ?? {})?.some(([_, value]) => value);
      const hasAnswerValues = order?.answer?.some(({ answerRow }) => answerRow?.length > 0);

      return !!(showPersonDetails && (hasPersonValues || hasCompanyValues || hasInvoiceValues || hasAnswerValues));
    },
    [orders, showPersonDetails]
  );

  return (
    <div className="w-full flex flex-col">
      {orders.map((o, i) => {
        return (
          <div key={o?.orderId} className="w-full block">
            {showConfPersonDetails(o) && (
              <div className="cms-order-summary-card">
                <PersonDetailsSummary order={o} {...props} />
              </div>
            )}
            {showOrderDetails && (
              <>
                {o?.orderRow && (
                  <div className="cms-table-container">
                    <table className="cms-table small overflow-x-auto" aria-label="spanning table">
                      {o?.orderRow?.length === 1 &&
                      o?.orderRow?.[0]?.eventId === mainEvent.eventId &&
                      !displayConfirmationSettings['ShowConfMainEvent'] ? null : (
                        <thead>
                          <TableHeader {...{ displayConfirmationSettings: overrideDisplay ? overridenDisplaySettings : displayConfirmationSettings }} />
                        </thead>
                      )}

                      <tbody>
                        {[...flatOrderRowsList]
                          ?.filter(x => x.orderId === o.orderId)
                          ?.sort((a, b) => a.sortOrder - b.sortOrder)
                          ?.map(row => (
                            <Fragment key={row.eventId ?? row?.orderRowId}>
                              <ConfirmationOrderRow
                                {...props}
                                displayConfirmationSettings={overrideDisplay ? overridenDisplaySettings : displayConfirmationSettings}
                                mainEvent={mainEvent}
                                orderRow={row}
                              />
                            </Fragment>
                          ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </>
            )}
          </div>
        );
      })}
      {showHotelDetails && flatOrderRowsList?.some(or => or.hotelGroupId) && (
        <HotelDetails
          {...props}
          hotelAllotments={hotelAllotments}
          hotels={hotels}
          displayConfirmationSettings={overrideDisplay ? overridenDisplaySettings : displayConfirmationSettings}
        />
      )}
      {showConfOrderTotalSum && <OrderTotalSummary {...props} flatOrderRows={flatOrderRowsList.filter(or => orders.some(o => o.orderId === or.orderId))} />}
      {showPaymentDetails && ordersLoaded && <PaymentDetails activePersonNo={props.activePersonNo} orders={props.orders} environment={props.environment} />}
    </div>
  );
});
